import type React from "react";
import { useState } from "react";
import { TableCell, TableRow, IconButton } from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import Table from "./Table";
import SourceChip from "./SourceChip";

interface SimpleEntity {
  _id: string;
  name: string;
  initiallyCreatedFrom?: "MANUAL" | "LOGISLY";
}

interface SimpleNameTableProps<T extends SimpleEntity> {
  items: T[];
  loading?: boolean;
  onEdit?: (id: string, newName: string) => Promise<void>;
  canEdit?: boolean;
  showSource?: boolean;
  EditFormComponent: React.ComponentType<{
    open: boolean;
    onClose: () => void;
    onSubmit: (data: { name: string }) => Promise<void>;
    error: string | null;
    initialData: { name: string };
    isEditing: boolean;
  }>;
}

function SimpleNameTable<T extends SimpleEntity>({
  items,
  loading,
  onEdit,
  canEdit = false,
  showSource = false,
  EditFormComponent,
}: SimpleNameTableProps<T>) {
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const columns = [
    { id: "name", label: "Name" },
    ...(showSource ? [{ id: "source", label: "Source" }] : []),
    { id: "actions", label: "Actions", align: "center" as const },
  ];

  const handleEditClick = (item: T) => {
    setEditingItem(item);
  };

  const handleClose = () => {
    setEditingItem(null);
  };

  const handleSubmit = async (data: { name: string }) => {
    if (editingItem && onEdit) {
      await onEdit(editingItem._id, data.name);
      handleClose();
    }
  };

  return (
    <>
      <Table
        data={items}
        columns={columns}
        loading={loading}
        showSearch={false}
        renderRow={(item) => (
          <TableRow key={item._id}>
            <TableCell>{item.name}</TableCell>
            {showSource && (
              <TableCell>
                <SourceChip source={item.initiallyCreatedFrom} />
              </TableCell>
            )}
            <TableCell align="center">
              <IconButton
                size="small"
                onClick={() => handleEditClick(item)}
                color="primary"
                disabled={!canEdit}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </TableCell>
          </TableRow>
        )}
      />
      {editingItem && (
        <EditFormComponent
          open={true}
          onClose={handleClose}
          onSubmit={handleSubmit}
          error={null}
          initialData={{ name: editingItem.name }}
          isEditing={true}
        />
      )}
    </>
  );
}

export default SimpleNameTable;
